/*
 * INCIDENTS (F4a) — a Founder-recorded operational log. No member data: titles are written by
 * Console staff. Every change is stored together with its own audit entry on the server.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { AlertTriangle } from "lucide-react";
import { Panel, Note, Chip, fmt } from "./ConsolePrimitives";
import { HistoryToggle } from "./HistoryToggle";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/founder/incidents`;
const SEVERITIES = ["sev1", "sev2", "sev3"];
const STATUSES = ["open", "monitoring", "resolved"];
const SURFACES = ["message", "talk", "dreams", "memory", "billing", "account", "platform", "other"];
const TONE = { open: "warn", monitoring: "quiet", resolved: "good" };

const Select = ({ value, options, onChange, testId }) => (
  <select className="fc-input" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}>
    {options.map((o) => <option key={o} value={o}>{o}</option>)}
  </select>
);

const NewIncident = ({ onSaved }) => {
  const [form, setForm] = useState({ title: "", severity: "sev2", surface: "platform" });
  const [error, setError] = useState(null);
  const save = async () => {
    setError(null);
    try {
      await axios.post(API, form);
      setForm({ title: "", severity: "sev2", surface: "platform" });
      onSaved();
    } catch (e) {
      setError(e?.response?.data?.detail || "The incident could not be recorded.");
    }
  };
  return (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 14 }}>
      <input className="fc-input" style={{ flex: "1 1 260px" }} maxLength={120} value={form.title}
        placeholder="What is happening (no member details)"
        onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="fc-incident-title-input" />
      <Select value={form.severity} options={SEVERITIES} onChange={(v) => setForm({ ...form, severity: v })} testId="fc-incident-severity-select" />
      <Select value={form.surface} options={SURFACES} onChange={(v) => setForm({ ...form, surface: v })} testId="fc-incident-surface-select" />
      <button className="fc-btn" onClick={save} disabled={!form.title.trim()} data-testid="fc-incident-create-btn">Record incident</button>
      {error && <Note testId="fc-incident-create-error">{error}</Note>}
    </div>
  );
};

export const IncidentsView = ({ canManage }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(API);
      setData(res.data);
    } catch (e) {
      setError("The incident log could not be read just now.");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/${encodeURIComponent(id)}`, { status });
      load();
    } catch (e) {
      setError("That change was refused.");
    }
  };

  return (
    <>
      <header className="fc__section-head">
        <h2 data-testid="fc-incidents-title">Incidents</h2>
        <p>Open now: <b data-testid="fc-incidents-open">{fmt(data?.incidents_open?.value)}</b></p>
      </header>
      <Panel title="Incident log" icon={AlertTriangle} testId="fc-incidents-panel" sub={data?.boundary}>
        {canManage && <NewIncident onSaved={load} />}
        <table className="fc-table">
          <thead><tr><th>Incident</th><th>Severity</th><th>Surface</th><th>Status</th><th>Started</th><th>Changes</th></tr></thead>
          <tbody>
            {(data?.incidents || []).map((i) => (
              <tr key={i.id} data-testid={`fc-incident-row-${i.id}`}>
                <td><b>{i.title}</b></td>
                <td>{i.severity}</td>
                <td>{i.surface}</td>
                <td>
                  {canManage
                    ? <Select value={i.status} options={STATUSES} onChange={(v) => setStatus(i.id, v)} testId={`fc-incident-status-${i.id}`} />
                    : <Chip tone={TONE[i.status]} testId={`fc-incident-status-${i.id}`}>{i.status}</Chip>}
                </td>
                <td>{(i.started_at || "").slice(0, 16).replace("T", " ")}</td>
                <td data-testid={`fc-incident-audit-${i.id}`}>
                  {i.audit_entries}
                  <HistoryToggle url={`${API}/${encodeURIComponent(i.id)}/history`} testId={`fc-incident-history-${i.id}`}
                    describe={(h) => `${h.action} (${(h.changes || []).join(", ")}) → ${h.status || "—"}`} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {data && !(data.incidents || []).length && <Note testId="fc-incidents-empty">No incident has been recorded.</Note>}
        {error && <Note testId="fc-incidents-error">{error}</Note>}
      </Panel>
    </>
  );
};
